// src/components/FaxProgress.jsx
// In-place progress bar for an outgoing fax that is still on its way.
// Stages come from the fax carrier (recorded by the backend webhook):
//   queued → processed (document prepared) → sending
// While sending, the bar keeps creeping forward based on elapsed time and
// page count, but never reaches the end on its own — only the real
// Delivered/Failed result replaces it. Faxes with no recorded stage fall
// back to elapsed time since they were sent.
import { useEffect, useState } from 'react';

const STAGES = {
  queued   : { label: 'Queued',              from: 5,  to: 20 },
  processed: { label: 'Preparing',           from: 25, to: 40 },
  sending  : { label: 'Sending',             from: 45, to: 95 },
  // Incoming: the carrier only reports that receiving started, so this
  // stage is timing-based from start to finish
  receiving: { label: 'Receiving',           from: 5,  to: 95 },
};

// Rough transmission time: ~30 s per page, at least 30 s
function expectedSendMs(pages) {
  return Math.max(30, (Number(pages) || 1) * 30) * 1000;
}

export function progressFor(fax, now) {
  const stage = STAGES[fax.stage] ? fax.stage : 'sending';
  const { from, to, label } = STAGES[stage];
  const since = Date.parse(fax.stage ? fax.stageAt : fax.sentAt) || now;
  const scale = stage === 'sending' ? expectedSendMs(fax.pages)
              : stage === 'receiving' ? 90000
              : 20000;
  // Eases toward `to` without reaching it
  const pct   = from + (to - from) * (1 - Math.exp(-Math.max(0, now - since) / scale));
  return { pct: Math.round(pct), label: fax.stage ? label : 'Sending' };
}

export default function FaxProgress({ fax, compact }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const { pct, label } = progressFor(fax, now);
  const pagesText = fax.pages ? ` · ${fax.pages} page${fax.pages > 1 ? 's' : ''}` : '';

  return (
    <div style={{ ...S.wrap, width: compact ? 120 : 220 }}
      role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}
      aria-label={`Fax ${label.toLowerCase()}, about ${pct}% done`}>
      <div style={S.labelRow}>
        <span style={S.label}>{label}…</span>
        <span style={S.pct}>{pct}%</span>
      </div>
      <div style={S.track}>
        <div style={{ ...S.fill, width: `${pct}%` }}>
          <div style={S.shine} />
        </div>
      </div>
      {!compact && <div style={S.sub}>Updates automatically{pagesText}</div>}
    </div>
  );
}

const S = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 4, maxWidth: '100%' },
  labelRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 },
  label: { fontSize: 11, fontWeight: 700, color: 'var(--t-1565c0)', textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap' },
  pct: { fontSize: 11, fontWeight: 600, color: 'var(--t-546e7a)', fontVariantNumeric: 'tabular-nums' },
  track: { height: 6, borderRadius: 6, background: 'var(--c-e3f2fd)', overflow: 'hidden' },
  fill: {
    position: 'relative', height: '100%', borderRadius: 6, overflow: 'hidden',
    background: 'linear-gradient(90deg, var(--c-42a5f5), var(--c-1565c0))', transition: 'width 1s linear'
  },
  shine: {
    position: 'absolute', inset: 0,
    background: 'linear-gradient(90deg, transparent, rgb(var(--c-ffffff-rgb) / 0.55), transparent)',
    animation: 'faxShine 1.4s ease-in-out infinite'
  },
  sub: { fontSize: 11, color: 'var(--t-78909c)' },
};
